import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Loader2, Sparkles, GraduationCap,
  Calendar, Users, Filter, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { CardBanner } from './CardBanner';

const SPORTS = [
  { id: 'all', label: 'All sports' },
  { id: 'football', label: 'Football' },
  { id: 'padel', label: 'Padel' },
  { id: 'pickleball', label: 'Pickleball' },
  { id: 'cricket', label: 'Cricket' },
  { id: 'other', label: 'Other' },
];
const LEVELS = [
  { id: 'all', label: 'All levels' },
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'all_levels', label: 'All levels (mixed)' },
];

const SPORT_COLOR = {
  football: '#16a34a',
  padel: '#0ea5e9',
  pickleball: '#f59e0b',
  cricket: '#dc2626',
  other: '#6b7280',
};
const sportColor = (s) => SPORT_COLOR[s] || '#6b7280';
const sportLabel = (s, other) => {
  if (s === 'other') return other || 'Other';
  return SPORTS.find(x => x.id === s)?.label || s;
};
const levelLabel = (l) => {
  const map = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', all_levels: 'All levels' };
  return map[l] || l;
};
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const fmtDays = (days = []) => days.map(d => DAY_SHORT[d] || d).join(' & ');
const fmtTime12 = (t) => {
  if (!t || !t.includes(':')) return t || '';
  const [h, m] = t.split(':');
  const hi = parseInt(h, 10);
  const suf = hi < 12 ? 'AM' : 'PM';
  const h12 = (hi % 12) || 12;
  return `${h12}:${m} ${suf}`;
};

const BADGE_TONE = {
  open: 'bg-emerald-100 text-emerald-700',
  full: 'bg-red-100 text-red-700',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-gray-100 text-gray-500',
};
const BADGE_LABEL = { open: 'Open', full: 'Full', ended: 'Ended', cancelled: 'Cancelled' };

// ============== Listing page ==============

const CoachingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [sport, setSport] = useState(searchParams.get('sport') || 'all');
  const [level, setLevel] = useState(searchParams.get('level') || 'all');
  const [venueId, setVenueId] = useState(searchParams.get('venue') || 'all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sport !== 'all') params.set('sport', sport);
      if (level !== 'all') params.set('level', level);
      if (venueId !== 'all') params.set('venue_id', venueId);
      const res = await fetch(`${API_URL}/api/coaching/public/list?${params.toString()}`);
      const data = await res.json();
      setProgrammes(Array.isArray(data) ? data : []);
    } catch {
      setProgrammes([]);
    } finally {
      setLoading(false);
    }
  }, [sport, level, venueId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_URL}/api/facilities/public`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setVenues(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg" data-testid="coaching-back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight">Coaching</h1>
            <p className="text-xs text-gray-500 -mt-0.5">Expert coaching programmes near you</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-testid="coaching-filters">
          <div className="relative">
            <Filter className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={sport}
              onChange={e => setSport(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-full bg-white border appearance-none"
              data-testid="filter-sport"
            >
              {SPORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="relative">
            <Sparkles className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={level}
              onChange={e => setLevel(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-full bg-white border appearance-none"
              data-testid="filter-level"
            >
              {LEVELS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>
          <div className="relative">
            <MapPin className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={venueId}
              onChange={e => setVenueId(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-full bg-white border appearance-none"
              data-testid="filter-venue"
            >
              <option value="all">All venues</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </div>

        {/* Programmes grid */}
        <div className="mt-5">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : programmes.length === 0 ? (
            <div className="bg-white rounded-2xl border p-10 text-center" data-testid="coaching-empty">
              <GraduationCap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No coaching programmes found</p>
              <p className="text-xs text-gray-400 mt-1">Try different filters or check back later.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coaching-grid">
              {programmes.map(p => (
                <CoachingCard key={p.id} programme={p} onClick={() => navigate(`/coaching/${p.slug}`)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== Card (reused on Explore + VenueDetail) ==============

export const CoachingCard = ({ programme, onClick }) => {
  const badge = programme.registration_badge || 'open';
  const spots = programme.cheapest_spots_left ?? 0;
  const isFree = programme.is_free;
  const priceLabel = isFree ? 'Free' : `From ₹${programme.from_price || 0}`;

  return (
    <button
      onClick={onClick}
      className="text-left bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col w-full"
      data-testid={`coaching-card-${programme.slug}`}
    >
      {/* Banner / placeholder */}
      <div className="aspect-video relative" style={{ backgroundColor: sportColor(programme.sport) }}>
        <CardBanner
          src={programme.banner_image_url ? `${API_URL}${programme.banner_image_url}` : null}
          Icon={GraduationCap}
          gradient={`linear-gradient(135deg, ${sportColor(programme.sport)} 0%, #0A2C93 160%)`}
          testId={`coaching-banner-${programme.slug}`}
        />
        <span
          className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${BADGE_TONE[badge] || BADGE_TONE.open}`}
          data-testid={`badge-${programme.slug}`}
        >
          {BADGE_LABEL[badge] || badge}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-bold text-base text-gray-900 line-clamp-2 leading-tight">{programme.title}</h3>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <MapPin className="h-3 w-3" />{programme.venue_name}
        </p>

        {/* Sport + Level badges */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-white"
            style={{ backgroundColor: sportColor(programme.sport) }}
            data-testid={`badge-sport-${programme.slug}`}
          >
            {sportLabel(programme.sport, programme.other_sport_name)}
          </span>
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#0A2C93]/10 text-[#0A2C93]"
            data-testid={`badge-level-${programme.slug}`}
          >
            {levelLabel(programme.level)}
          </span>
        </div>

        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3 w-3" />{fmtDays(programme.days_of_week)} · {fmtTime12(programme.start_time)}
        </p>
        {programme.coach_name && (
          <p className="text-xs text-gray-500">Coach · {programme.coach_name}</p>
        )}

        <div className="flex items-center justify-between mt-auto pt-2">
          <span className={`font-bold text-sm ${isFree ? 'text-emerald-600' : ''}`}>{priceLabel}</span>
          <span className="flex items-center gap-1 text-xs font-semibold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-full">
            View <ChevronRight className="h-3 w-3" />
          </span>
        </div>

        {badge === 'open' && spots > 0 && spots <= 5 && (
          <p className="text-[11px] text-amber-700 flex items-center gap-1">
            <Users className="h-3 w-3" /> Only {spots} spots left on cheapest package
          </p>
        )}
      </div>
    </button>
  );
};

export default CoachingPage;
